import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { css } from '@emotion/react'
import { mobile, tablet, desktopUp } from '@styles/global'
import { NextButton, Heading } from '@components/ChecklistSlide'
import useTranslatedString from '@utils/useTranslatedString'
import { useSiteState } from "@context/siteContext"
import isArabic from '@utils/isArabic'

const ChecklistQuestion = ({ className, slide, next, handleAnswer, checklist, slides }) => {
	const currentSection = slide.sections?.find(section => section.title === slide.section)
	const currentSectionIndex = slide.sections?.findIndex(section => section.title === slide.section)
	const [ siteState ] = useSiteState()
	const language = siteState?.checklistLanguage 
	const options = 
		slide?.question?.multiAnswers?.length > 0 ? 
			slide?.question?.multiAnswers.map((a, i) => ({
				value: slide?.question?.answers[i],
				text: a
			})) : 
			[
				{
					value: 'never',
					text: useTranslatedString('never')
				}, 
				{
					value: 'sometimes',
					text: useTranslatedString('sometimes')
				}, 
				{
					value: 'often',
					text: useTranslatedString('often')
				}, 
				{
					value: 'constantly',
					text: useTranslatedString('constantly')
				}
			]
	const [value, setValue] = useState()
	const allQuestions = slide.sections.map(section => section.questions)?.flat()

	const values = {
		never: 1,
		sometimes: 2,
		often: 3,
		constantly: 4
	}

	const trackingData = {
		event: 'custom.form.page.complete',
		action: 'page complete',
		type: slide.type,
		text: 'Continue',
		section: {
			name: currentSection.title,
			number: currentSectionIndex + 2,
			total: 5
		},
		question:{
			totalsection: currentSection?.questions?.length,
			total: slide.sections.map(section => section.questions)?.flat()?.length,
			number: currentSection?.questions?.findIndex(q => q.text === slide.question.text) + 1,
			numberTotal: allQuestions.findIndex(q => q.text === slide.question.text) + 1,
			name: slide.question.text,
			name_translated: slide.question.multiText,
			answer: value,
			answer_number: values[value]
		},
		page: {
			number: slides?.findIndex(s => s.question === slide.question) + 1,
			numberTotal: slides?.length,
		},
		checklist: checklist?.slug,
		language: siteState?.checklistLanguage
	}

	useEffect(() => {
		if(slide.userData){
			const answerInData = slide.userData?.answers?.find(a => a.question.text === slide.question.text)
			if(answerInData){
				setValue(answerInData.value)
			}
		}
	}, [slide.userData?.answers])

	const handleInternalAnswer = option => {
		setValue(option)
		// console.log(option)
		handleAnswer({value: option, question: slide.question, section: slide.section, sectionEn: slide.sectionEn})
		window.setTimeout(() => {
			next(trackingData)
		}, 400)
	}

	return (
		<Wrap className={className}>
			{language === 'en' &&
				<Heading>{slide.index + 1} of {currentSection.questions?.length}</Heading>
			}
			<Text>
				<h6 dir={isArabic(slide.question.multiText) ? 'rtl' : 'ltr'}>{slide.question.multiText}</h6>
				<p dir={isArabic(slide.question.multiSubText) ? 'rtl' : 'ltr'}>{slide.question.multiSubText}</p>
			</Text>
			<Answers nonStandard={slide?.question?.answers?.length > 0}>
				{options?.map(option => 
					<Answer 
						key={option.text} 
						onClick={() => handleInternalAnswer(option.value)} 
						active={value === option.value}
						nonStandard={slide?.question?.answers?.length > 0}
						>
						{option.text}
					</Answer>
				)}
				{slide?.question?.answers?.length > 0 &&
					<Answer inverse nonStandard onClick={() => handleInternalAnswer('undefined')}>{useTranslatedString('skip')}</Answer>
				}
			</Answers>
			{/* <NextButton onClick={() => } bgColor='bteal60' active={value} checklist>Continue</NextButton> */}
		</Wrap>
	)
}

const Wrap = styled.div`
	text-align: center;
	/* padding: var(--m); */
`

const Text = styled.div`
	margin: var(--xl) auto var(--xl);
	max-width: 500px;
	min-height: 160px;
	h6{
		margin-bottom: 0.6em;
	}
	${mobile}{
		margin: var(--s) auto var(--m);
	}
`

const Answers = styled.div`
	display: flex;
	margin-bottom: var(--xl);
	gap: 5px;
	justify-content: center;
	flex-wrap: ${props => props?.nonStandard ? 'wrap': 'none'};
	${mobile}{
		display: block;
		margin-bottom: 0;
	}
`

const Answer = styled.button`
	border-right: none;
	width: ${props => props?.nonStandard ? 'none': '100%'};
	display: inline-block;
	padding: var(--xs);
	background: ${props => props.active ? 'var(--dteal)' : props.inverse ? 'var(--white)' : 'var(--bteal60)'};
	border: 3px solid ${props => props.active ? 'var(--dteal)' : props.inverse ? 'var(--dteal)' : 'var(--bteal60)'};
	color: ${props => props.active ? 'var(--white)' : 'inherit'};
	text-transform: capitalize;
	font-weight: 700;
	border-radius: 4px;
	min-width: ${props => props?.nonStandard ? '130px': 'none'};
	box-sizing: border-box;
	${desktopUp}{
		&:hover{
			background: ${props => props.active ? 'var(--dteal)' : props.inverse ? 'var(--white)' : 'var(--bteal60)'};
			border: 3px solid var(--Panda-Dark-Teal-100, #11383A);
		}
	}
	${mobile}{
		/* border: 3px solid var(--dteal); */
		margin-bottom: 10px;
		/* border-bottom: none; */
		width: 100%;
		&:last-child{
			/* border-bottom: 3px solid var(--dteal); */
		}
	}
`

ChecklistQuestion.propTypes = {
	className: PropTypes.string,
	slide: PropTypes.object,
	checklist: PropTypes.object,
	next: PropTypes.func,
	handleAnswer: PropTypes.func,
	slides: PropTypes.array,
}

export default ChecklistQuestion